import { observer } from 'mobx-react';
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import './App.less';
import LayoutProvider from './views/Layout/LayoutProvider';
import { RootContext } from './RootProvider';
import './services/axiosInterceptor';
import { FourZeroFourView, LoadingView } from './views/Interstitial';
import NotificationProvider from './views/NotificationLayer/NotificationProvider';
import { useFeatureFlagStore } from 'tools/feature-flags/feature-flags.zustand';
import { FeatureFlagStore } from 'tools/feature-flags/feature-flags.types';
import Modal from 'react-modal';
import { ReactQueryDevtools } from 'react-query/devtools';

import { clearIndexedDB } from 'models/AuthModel';

import { useLDClient, withLDProvider, useFlags } from 'launchdarkly-react-client-sdk';
import { initializeAmplitude } from 'services/amplitudeHelper';
import { shouldInitializeAmplitude } from './utils/shouldInitializeAmplitude';
import { LDFeatureFlagEnum } from 'tools/feature-flags/launchdarkly-flags';
import { APIState } from './models/APIState';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/pt';
import { initialize } from 'tools/datadog/datadog';

Modal.setAppElement('#root');

const AMPLITUDE_API_KEY: string = import.meta.env.VITE_APP_AMPLITUDE_API_KEY || '';
const VITE_APP_TANS_STACK_QUERY = import.meta.env.VITE_APP_TANS_STACK_QUERY || false;

const App = observer(() => {
  const { auth, notifications } = React.useContext(RootContext);
  const ldClient = useLDClient();
  const { setLaunchDarklyFlags } = useFeatureFlagStore((state: FeatureFlagStore) => ({
    setLaunchDarklyFlags: state.setLaunchDarklyFlags,
  }));

  const flags = useFlags();
  const amplitudeInitialized = React.useRef(false);

  initialize();

  React.useEffect(() => {
    auth.login();
    clearIndexedDB();
  }, [auth]);

  // Store ALL LaunchDarkly flags in Zustand for non-React access
  React.useEffect(() => {
    const ldFlags: Record<string, boolean> = {};
    Object.values(LDFeatureFlagEnum).forEach((flagKey) => {
      ldFlags[flagKey] = flags[flagKey] ?? false;
    });
    setLaunchDarklyFlags(ldFlags);
  }, [flags, setLaunchDarklyFlags]);

  // Initialize Amplitude with feature flag control
  React.useEffect(() => {
    if (shouldInitializeAmplitude(amplitudeInitialized.current, flags, ldClient ?? null)) {
      amplitudeInitialized.current = true;
      const enableGuides = flags[LDFeatureFlagEnum.ENABLE_AMPLITUDE_GUIDES];
      initializeAmplitude(AMPLITUDE_API_KEY, enableGuides);
    }
  }, [flags]);

  React.useEffect(() => {
    if (!auth.userEmail) {
      return;
    }
    ldClient?.identify({ kind: 'user', key: auth.userId ?? '', email: auth.userEmail });
  }, [auth.userEmail, auth.userId, ldClient]);

  let content = <LoadingView />;
  switch (auth.status) {
    case APIState.FAIL:
      content = <FourZeroFourView relogin />;
      break;
    case APIState.SUCCESS:
      content = (
        <BrowserRouter>
          <NotificationProvider viewModel={notifications}>
            <LayoutProvider />
          </NotificationProvider>
        </BrowserRouter>
      );
      break;
    default:
      break;
  }

  return (
    <>
      {VITE_APP_TANS_STACK_QUERY && <ReactQueryDevtools initialIsOpen={false} />}
      <div className="App">{content}</div>
    </>
  );
});

export default withLDProvider({
  clientSideID: import.meta.env.VITE_APP_LAUNCHDARKLY_CLIENT_ID ?? '',
})(App);
